/**
 * AttestationHistoryPanel — Phase 07.3 polish (NEW component).
 *
 * Surfaces the per-engine attestation trail returned by the backend in
 * `EngineReviewStatus.attestation_history`. The data has been computed
 * by Phase 02 + 06 + 07.2 (status transitions + override events get
 * appended) but was previously not surfaced anywhere on the UI — the
 * external attester had no way to see what had happened on an engine
 * before they opened it.
 *
 * Design intent:
 *   - Collapsible (defaults to collapsed) so the panel doesn't bloat
 *     for engines with long histories.
 *   - Newest-first ordering (matches the typical user expectation when
 *     opening a history view).
 *   - Action chip + actor name + relative timestamp + optional note.
 *   - Severity color tied to action category (approved=green,
 *     disputed=red, waived=amber, override_applied=purple, system=gray).
 *
 * Out of scope for 07.3-polish:
 *   - Diff viewer for `metadata.before` / `metadata.after`.
 *   - Filter chips by action type.
 *   - Pagination (history lists are bounded by SM1's transition graph;
 *     the natural ceiling is ~dozens of entries per engine).
 */

import React, { useId, useState } from 'react';

import type { AttestationHistoryEntry } from '../../../types/engineReview';

export interface AttestationHistoryPanelProps {
  history?: AttestationHistoryEntry[];
  /** Default expansion state — defaults to collapsed for clean header. */
  defaultExpanded?: boolean;
}

const ACTION_LABEL: Record<AttestationHistoryEntry['action'], string> = {
  approved: 'Approved',
  disputed: 'Disputed',
  rerun_requested: 'Rerun requested',
  rerun_completed: 'Rerun completed',
  waived: 'Waived',
  override_applied: 'Override applied',
};

const ACTION_CHIP_STYLE: Record<AttestationHistoryEntry['action'], React.CSSProperties> = {
  approved: { backgroundColor: 'transparent', color: 'var(--color-verified)' },
  disputed: { backgroundColor: 'var(--color-blocker-tint)', color: 'var(--color-blocker)' },
  rerun_requested: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  rerun_completed: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  waived: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  override_applied: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
};

const ACTOR_ROLE_LABEL: Record<AttestationHistoryEntry['actor_role'], string> = {
  external_attester: 'witness CPA',
  in_house_accountant: 'in-house staff',
  system: 'system',
};

function formatRelative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const deltaMs = Date.now() - then;
  const seconds = Math.round(deltaMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  // Falls back to the absolute date once it's old enough that "Nd ago"
  // stops being useful.
  return new Date(then).toLocaleDateString();
}

export default function AttestationHistoryPanel({
  history,
  defaultExpanded = false,
}: AttestationHistoryPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Unique per-instance id for aria-controls (Phase 07.8).
  const detailId = useId();

  if (!history || history.length === 0) {
    return null;
  }

  // Newest-first ordering. Sort defensively rather than trusting backend
  // order — the field is a plain List and the spec doesn't guarantee an
  // order contract.
  const sorted = [...history].sort((a, b) => {
    const ta = Date.parse(a.timestamp);
    const tb = Date.parse(b.timestamp);
    return tb - ta;
  });

  return (
    <div
      className="rounded-lg border p-3"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}
      data-testid="attestation-history"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={expanded}
        aria-controls={detailId}
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5"
            style={{ color: 'var(--color-text-secondary)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            Attestation history ({sorted.length})
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {expanded && (
        <ol
          id={detailId}
          className="mt-3 space-y-2"
          data-testid="attestation-history-list"
        >
          {sorted.map((entry, idx) => (
            <li
              key={`${entry.timestamp}-${idx}`}
              className="border rounded p-2 text-sm"
              style={{ borderColor: 'var(--color-border-sub)' }}
              data-testid={`attestation-history-entry-${entry.action}-${idx}`}
            >
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                <span
                  className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                  style={
                    ACTION_CHIP_STYLE[entry.action] ?? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                  }
                >
                  {ACTION_LABEL[entry.action] ?? entry.action}
                </span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {entry.actor_name}
                  <span style={{ color: 'var(--color-text-muted)' }}> ({ACTOR_ROLE_LABEL[entry.actor_role] ?? entry.actor_role})</span>
                </span>
                <span className="text-xs ml-auto" style={{ color: 'var(--color-text-muted)' }} title={entry.timestamp}>
                  {formatRelative(entry.timestamp)}
                </span>
              </div>
              {entry.note && (
                <p className="mt-1 text-xs whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>
                  {entry.note}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
